// Canopy shading: the plant canopy's filtering of sunlight and skylight, as a 3D texture.
//
// The simulation filters light through the plants and publishes, for every 1 m voxel, the
// fraction of the direct beam and of the skylight that survives (see light_field.py, "Canopy
// light"). This module holds that volume as two small 3D textures and provides the GLSL that
// scales a fragment's direct and ambient light by it, so what is drawn is lit by the same
// numbers a light sensor at that point reads. Because it is a volume, it shades everything
// that lives in the canopy: the ground and grass under a tree, and the tree itself, where the
// side facing away from the sun sits in the shadow of the crown's sun-facing side.
//
// Spectral: each volume is RGB. The canopy absorbs red and blue more than green, so shade is
// green-tinted, which the sensors see too.
//
// Why a texture and not a shadow map: plant cells are far too many to render into the sun's
// depth map each frame (see the note on particles in DefaultSimRenderer). Hills and rocks
// still cast through the ordinary shadow map; this covers the plants.
//
// Easing. The simulation republishes the volume about once a second (a growing crown, or the sun
// moving its shadow by a voxel, changes it). Swapping the texture at each arrival makes the
// whole canopy's shade pop in one frame, a step the eye is very good at catching, while the
// ground shadows from the hills glide with the sun. So the newest volume is only a target: a
// second, persistent pair of half-float 3D render targets holds what is drawn, and each frame a
// tiny GPU pass (one quad per depth slice, 16 of them at 128 x 128) moves every voxel a
// fraction of the way toward the target. The shaders still do exactly two 3D fetches. The
// upload path reuses its textures (no per-message allocation, no per-message GC), and where
// the GPU cannot render to half-float 3D targets it falls back to showing the target directly.
//
// Resolution is the simulation's voxel (1 m): it resolves crowns and stands, not single
// branches. Sim (x, y, z) maps to three (x, z, -y); voxel (i, j, k) covers sim x in [i, i+1),
// sim y in [j, j+1), sim z in [k, k+1).
import * as THREE from 'three';

export interface CanopyLightMessage {
  type: 'canopy_light';
  generation: number;
  nx: number; ny: number; nz: number;
  data: string;          // base64 of zlib data: uint8 (6, nz, ny, nx): direct R G B, then sky R G B
}

/** GLSL declarations: include in the fragment shader's common section. */
export const CANOPY_SHADE_DECL = /* glsl */`
uniform highp sampler3D uCanopyDir;
uniform highp sampler3D uCanopySky;
uniform vec4 uCanopyInfo;     // (1 / world width, 1 / world height, 1 / world depth, 1 when a volume has arrived)
uniform float uCanopyFlash;   // 0..1: how much of the direct term is a hand light (flashlight) that the canopy does not filter
`;

/**
 * GLSL statement block: scale the lit result by the canopy at a world position. Place after
 * `#include <lights_fragment_end>`. `posExpr` is a vec3 expression for the fragment's
 * three-space position (x, height, z).
 */
export function canopyShadeApply(posExpr: string): string {
  return /* glsl */`
{
  vec3 cP = ${posExpr};
  vec3 cUvw = vec3(cP.x * uCanopyInfo.x, -cP.z * uCanopyInfo.y, cP.y * uCanopyInfo.z);
  // The volume is 1 m voxels. Plain trilinear filtering reconstructs it with straight ramps whose
  // creases sit on the voxel grid, which shows up as blocky diamonds and triangles in the shade.
  // Bending the filter weight through a smoothstep makes the reconstruction smooth at the voxel
  // centres too (no creases), for the same two fetches: the hardware still does the blend.
  vec3 cSz = vec3(textureSize(uCanopyDir, 0));
  vec3 cQ = cUvw * cSz - 0.5;
  vec3 cI = floor(cQ);
  vec3 cF = cQ - cI;
  cF = cF * cF * (3.0 - 2.0 * cF);
  vec3 cS = (cI + 0.5 + cF) / cSz;
  vec3 cDir = mix(vec3(1.0), texture(uCanopyDir, cS).rgb, uCanopyInfo.w);
  vec3 cSky = mix(vec3(1.0), texture(uCanopySky, cS).rgb, uCanopyInfo.w);
  // The canopy filters the sun's beam. A flashlight is not the sun: with the beam gone (night) the whole
  // direct term is the flashlight, so the filter is lifted by the same amount.
  cDir = mix(cDir, vec3(1.0), uCanopyFlash);
  reflectedLight.directDiffuse   *= cDir;
  reflectedLight.directSpecular  *= cDir;
  reflectedLight.indirectDiffuse *= cSky;
  reflectedLight.indirectSpecular *= cSky;
}
`;
}


const BLIT_VERT = /* glsl */`
void main() { gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
// One output texel per fragment: read the same texel of the displayed volume and of the target
// (integer fetches, so no filtering question) and move a fraction uK of the way.
const BLIT_FRAG = /* glsl */`
precision highp float;
precision highp int;
precision highp sampler3D;      // GLSL ES 3.00 has no default for sampler3D; some drivers refuse to compile without it
uniform sampler3D uPrev;
uniform sampler3D uTarget;
uniform float uK;
uniform int uLayer;
void main() {
  ivec3 p = ivec3(ivec2(gl_FragCoord.xy), uLayer);
  gl_FragColor = mix(texelFetch(uPrev, p, 0), texelFetch(uTarget, p, 0), uK);
}
`;

// Blur of a freshly landed volume, one depth slice at a time, in the horizontal plane only (the
// ground is what shows the blockiness, and trilinear filtering already smooths the vertical). A 5 x 5
// binomial kernel, about one voxel of standard deviation. Runs once per message, not per frame.
const BLUR_FRAG = /* glsl */`
precision highp float;
precision highp int;
precision highp sampler3D;
uniform sampler3D uSrc;
uniform int uLayer;
uniform ivec2 uSize;
float bw(int k) { return k == 0 ? 6.0 : ((k == 1 || k == -1) ? 4.0 : 1.0); }
void main() {
  ivec2 c = ivec2(gl_FragCoord.xy);
  vec4 s = vec4(0.0);
  for (int j = -2; j <= 2; j++) {
    for (int i = -2; i <= 2; i++) {
      ivec2 q = clamp(c + ivec2(i, j), ivec2(0), uSize - ivec2(1));
      s += bw(i) * bw(j) * texelFetch(uSrc, ivec3(q, uLayer), 0);
    }
  }
  gl_FragColor = s / 256.0;
}
`;

function makeVolume(data: Uint8Array, nx: number, ny: number, nz: number): THREE.Data3DTexture {
  const t = new THREE.Data3DTexture(data as Uint8Array<ArrayBuffer>, nx, ny, nz);
  t.format = THREE.RGBAFormat;
  t.type = THREE.UnsignedByteType;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  t.unpackAlignment = 1;
  t.colorSpace = THREE.NoColorSpace;      // transmittance is linear data, not a colour
  t.needsUpdate = true;
  return t;
}

function makeTarget(nx: number, ny: number, nz: number): THREE.WebGL3DRenderTarget {
  const rt = new THREE.WebGL3DRenderTarget(nx, ny, nz, {
    type: THREE.HalfFloatType, format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    depthBuffer: false, stencilBuffer: false, generateMipmaps: false,
  });
  const t = rt.texture;
  t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
  t.colorSpace = THREE.NoColorSpace;
  return rt;
}

function makeBlurTarget(nx: number, ny: number, nz: number): THREE.WebGL3DRenderTarget {
  const rt = new THREE.WebGL3DRenderTarget(nx, ny, nz, {
    type: THREE.UnsignedByteType, format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    depthBuffer: false, stencilBuffer: false, generateMipmaps: false,
  });
  const t = rt.texture;
  t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
  t.colorSpace = THREE.NoColorSpace;
  return rt;
}

async function inflate(b64: string): Promise<Uint8Array> {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** One volume (direct or sky): its target texture and the ping-pong pair that is drawn. */
interface Channel {
  data: Uint8Array;                        // RGBA8 target contents, rewritten per message
  target: THREE.Data3DTexture;             // the newest volume as uploaded (sharp)
  blur: THREE.WebGL3DRenderTarget | null;  // the same, blurred: what the easing (and the no-easing fallback) approaches
  rt: [THREE.WebGL3DRenderTarget, THREE.WebGL3DRenderTarget] | null;
}

export class CanopyShade {
  /** Shared uniform objects: hand to `Object.assign(shader.uniforms, shade.uniforms)`. */
  readonly uniforms: {
    uCanopyDir:  { value: THREE.Data3DTexture };
    uCanopySky:  { value: THREE.Data3DTexture };
    uCanopyInfo: { value: THREE.Vector4 };
    uCanopyFlash: { value: number };
  };
  /** Time constant (s) of the easing toward each new volume. Volumes arrive about once a second. */
  easeSeconds = 0.5;

  private readonly worldW: number;
  private readonly worldH: number;
  private latest = -1;          // generation of the newest volume accepted (messages can finish out of order)
  private nx = 0; private ny = 0; private nz = 0;
  private dir: Channel | null = null;
  private sky: Channel | null = null;
  private cur = 0;              // which half of each ping-pong pair holds the drawn volume
  private pending = false;      // a new target has landed and the drawn volume has not reached it
  private needsBlur = false;   // a new target has landed and its blurred copy has not been made yet
  private snap = false;         // next pass copies the target outright (first volume, or a size change)
  private sinceLoad = 0;        // seconds since the last target landed
  private smoothing: boolean | null = null;    // null until the first update() can ask the renderer
  private blurPass: { scene: THREE.Scene; cam: THREE.Camera; mesh: THREE.Mesh; mat: THREE.ShaderMaterial } | null = null;
  private blit: { scene: THREE.Scene; cam: THREE.Camera; mesh: THREE.Mesh; mat: THREE.ShaderMaterial } | null = null;
  private neutralDir: THREE.Data3DTexture;
  private neutralSky: THREE.Data3DTexture;

  /** `worldWidth` and `worldHeight` are the sim grid extent in metres (x and y). */
  constructor(worldWidth: number, worldHeight: number) {
    this.worldW = worldWidth; this.worldH = worldHeight;
    this.neutralDir = makeVolume(new Uint8Array(4).fill(255), 1, 1, 1);
    this.neutralSky = makeVolume(new Uint8Array(4).fill(255), 1, 1, 1);
    this.uniforms = {
      uCanopyDir:  { value: this.neutralDir },
      uCanopySky:  { value: this.neutralSky },
      uCanopyInfo: { value: new THREE.Vector4(1 / worldWidth, 1 / worldHeight, 1, 0) },
      uCanopyFlash: { value: 0 },
    };
  }

  /** True while the drawn volume is still moving toward the newest one. */
  get easing(): boolean { return this.pending; }

  /** Install a canopy_light message as the new target. Sizes come from the message, so a world of any size works. */
  async load(msg: CanopyLightMessage): Promise<void> {
    const { nx, ny, nz } = msg;
    const n = nx * ny * nz;
    let raw: Uint8Array;
    try {
      raw = await inflate(msg.data);
    } catch {
      console.warn('[sim] canopy_light: could not decompress, ignoring');
      return;
    }
    if (raw.length !== 6 * n) { console.warn('[sim] canopy_light: size mismatch, ignoring'); return; }
    if (msg.generation < this.latest) return;           // a newer volume already landed
    this.latest = msg.generation;

    const resized = nx !== this.nx || ny !== this.ny || nz !== this.nz || !this.dir || !this.sky;
    if (resized) {
      this.releaseVolumes();
      this.nx = nx; this.ny = ny; this.nz = nz;
      const mk = (): Channel => {
        const data = new Uint8Array(n * 4);
        return { data, target: makeVolume(data, nx, ny, nz), blur: null, rt: null };
      };
      this.dir = mk(); this.sky = mk();
      this.snap = true;
    }
    // Planes are (6, nz, ny, nx); a texel's index k is the same in every plane, so each RGBA
    // texel just gathers planes 0..2 (direct) or 3..5 (sky) at k. The arrays are reused, so a
    // message costs one pass over the voxels and a texture upload, and no allocation.
    const dir = this.dir!.data, sky = this.sky!.data;
    for (let k = 0; k < n; k++) {
      const o = k * 4;
      dir[o]     = raw[k];
      dir[o + 1] = raw[n + k];
      dir[o + 2] = raw[2 * n + k];
      dir[o + 3] = 255;
      sky[o]     = raw[3 * n + k];
      sky[o + 1] = raw[4 * n + k];
      sky[o + 2] = raw[5 * n + k];
      sky[o + 3] = 255;
    }
    this.dir!.target.needsUpdate = true;
    this.sky!.target.needsUpdate = true;
    this.pending = true;
    this.needsBlur = true;
    this.sinceLoad = 0;
  }

  /**
   * Advance the easing. Call once per frame, before rendering, with the frame's dt. Does nothing
   * unless a new volume is still being approached.
   */
  update(renderer: THREE.WebGLRenderer, dt: number) {
    if (!this.pending || !this.dir || !this.sky) return;
    if (this.needsBlur) {
      this.blurChannel(renderer, this.dir);
      this.blurChannel(renderer, this.sky);
      this.needsBlur = false;
    }
    if (this.smoothing === null) {
      const ext = renderer.extensions;
      this.smoothing = ext.has('EXT_color_buffer_float') || ext.has('EXT_color_buffer_half_float');
      if (!this.smoothing) console.warn('[sim] canopy easing unavailable (no float render targets); shade will step');
    }
    if (!this.smoothing) {
      // no easing: show the target as it lands
      this.uniforms.uCanopyDir.value = this.dir.blur!.texture as unknown as THREE.Data3DTexture;
      this.uniforms.uCanopySky.value = this.sky.blur!.texture as unknown as THREE.Data3DTexture;
      this.uniforms.uCanopyInfo.value.set(1 / this.worldW, 1 / this.worldH, 1 / this.nz, 1);
      this.pending = false;
      return;
    }
    this.sinceLoad += Math.max(0, Math.min(dt, 0.25));
    // Far enough along that what is left is below 8-bit resolution: land exactly and stop passing.
    const done = this.sinceLoad > 8 * this.easeSeconds;
    const k = this.snap || done ? 1 : 1 - Math.exp(-Math.max(0, dt) / this.easeSeconds);
    this.pass(renderer, this.dir, k);
    this.pass(renderer, this.sky, k);
    this.cur ^= 1;
    this.uniforms.uCanopyDir.value = this.dir.rt![this.cur].texture;
    this.uniforms.uCanopySky.value = this.sky.rt![this.cur].texture;
    this.uniforms.uCanopyInfo.value.set(1 / this.worldW, 1 / this.worldH, 1 / this.nz, 1);
    this.snap = false;
    if (done) this.pending = false;
  }

  /** blur = 5 x 5 blur of target, one depth slice at a time (run once per landed volume). */
  private blurChannel(renderer: THREE.WebGLRenderer, ch: Channel) {
    if (!ch.blur) ch.blur = makeBlurTarget(this.nx, this.ny, this.nz);
    if (!this.blurPass) {
      const mat = new THREE.ShaderMaterial({
        vertexShader: BLIT_VERT, fragmentShader: BLUR_FRAG,
        uniforms: { uSrc: { value: null }, uLayer: { value: 0 }, uSize: { value: new THREE.Vector2(1, 1) } },
        depthTest: false, depthWrite: false, blending: THREE.NoBlending,
      });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
      mesh.frustumCulled = false;
      const scene = new THREE.Scene(); scene.add(mesh);
      this.blurPass = { scene, cam: new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1), mesh, mat };
    }
    const { scene, cam, mat } = this.blurPass;
    mat.uniforms.uSrc.value = ch.target;
    mat.uniforms.uSize.value.set(this.nx, this.ny);
    const prevTarget = renderer.getRenderTarget();
    const prevFace = renderer.getActiveCubeFace();
    const prevMip = renderer.getActiveMipmapLevel();
    const prevAuto = renderer.autoClear;
    renderer.autoClear = false;
    for (let z = 0; z < this.nz; z++) {
      mat.uniforms.uLayer.value = z;
      renderer.setRenderTarget(ch.blur, z);
      renderer.render(scene, cam);
    }
    renderer.setRenderTarget(prevTarget, prevFace, prevMip);
    renderer.autoClear = prevAuto;
  }

  /** drawn[1 - cur] = mix(drawn[cur], target, k), one depth slice at a time. */
  private pass(renderer: THREE.WebGLRenderer, ch: Channel, k: number) {
    if (!ch.rt) ch.rt = [makeTarget(this.nx, this.ny, this.nz), makeTarget(this.nx, this.ny, this.nz)];
    if (!this.blit) {
      const mat = new THREE.ShaderMaterial({
        vertexShader: BLIT_VERT, fragmentShader: BLIT_FRAG,
        uniforms: { uPrev: { value: null }, uTarget: { value: null }, uK: { value: 1 }, uLayer: { value: 0 } },
        depthTest: false, depthWrite: false, blending: THREE.NoBlending,
      });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
      mesh.frustumCulled = false;
      const scene = new THREE.Scene(); scene.add(mesh);
      this.blit = { scene, cam: new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1), mesh, mat };
    }
    const { scene, cam, mat } = this.blit;
    const src = ch.rt[this.cur], dst = ch.rt[this.cur ^ 1];
    mat.uniforms.uPrev.value = src.texture;
    mat.uniforms.uTarget.value = ch.blur!.texture;
    mat.uniforms.uK.value = k;
    const prevTarget = renderer.getRenderTarget();
    const prevFace = renderer.getActiveCubeFace();
    const prevMip = renderer.getActiveMipmapLevel();
    const prevAuto = renderer.autoClear;
    renderer.autoClear = false;
    for (let z = 0; z < this.nz; z++) {
      mat.uniforms.uLayer.value = z;
      renderer.setRenderTarget(dst, z);       // for a 3D target the "face" argument selects the depth slice
      renderer.render(scene, cam);
    }
    renderer.setRenderTarget(prevTarget, prevFace, prevMip);
    renderer.autoClear = prevAuto;
  }

  private releaseVolumes() {
    for (const ch of [this.dir, this.sky]) {
      if (!ch) continue;
      ch.target.dispose();
      if (ch.blur) ch.blur.dispose();
      if (ch.rt) { ch.rt[0].dispose(); ch.rt[1].dispose(); }
    }
    this.dir = null; this.sky = null;
    this.cur = 0;
    // until the new volumes are installed, draw neutral light rather than a disposed texture
    this.uniforms.uCanopyDir.value = this.neutralDir;
    this.uniforms.uCanopySky.value = this.neutralSky;
    this.uniforms.uCanopyInfo.value.w = 0;
  }

  dispose() {
    this.releaseVolumes();
    this.neutralDir.dispose(); this.neutralSky.dispose();
    if (this.blit) { this.blit.mesh.geometry.dispose(); this.blit.mat.dispose(); this.blit = null; }
    if (this.blurPass) { this.blurPass.mesh.geometry.dispose(); this.blurPass.mat.dispose(); this.blurPass = null; }
  }
}
